import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";
import { OutfitCombinationResponse } from "../../types";
import { ValidationResult, ValidationProduct } from "../../components/ConflictResolutionModal";
import ConflictResolutionModal from "../../components/ConflictResolutionModal";
import CombinationCard from "../../components/CombinationCard";
import LoadingSpinner from "../../components/LoadingSpinner";
import ImageZoomModal from "../../components/ImageZoomModal";
import GenerateButton from "../../components/GenerateButton";
import ErrorMessage from "../../components/ErrorMessage";
import EmptyState from "../../components/EmptyState";
import Disclaimer from "../../components/Disclaimer";
import MenuButton from "../../components/MenuButton";
import EditProductsModal, { Product as ProductType } from "../../components/EditProductsModal";
import PlaceDropdown from "../../components/PlaceDropdown";
import { MatchOutfitSelector } from "./components/MatchOutfitSelector/MatchOutfitSelector";
import FashMatchService, { CategorizedItems } from "../../services/FashMatchService";
import { environmentConfig } from "../../config/environment";
import "./FashMatchPage.css";

const FashMatchPage: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<OutfitCombinationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState("Ürünler inceleniyor...");
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<string | undefined>(undefined);

  // Validation states
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [showConflictModal, setShowConflictModal] = useState(false);

  // New UI states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [categorizedItems, setCategorizedItems] = useState<CategorizedItems | null>(null);
  const [selectorInitialSelection, setSelectorInitialSelection] = useState<string[]>([]);
  const [isPlaceDropdownOpen, setIsPlaceDropdownOpen] = useState(false);

  // Store original products list (so we don't lose products when editing)
  const [availableProducts, setAvailableProducts] = useState<ProductType[]>([]);

  const basketItems = useMemo(
    () => state.basketItems || [],
    [state.basketItems]
  );
  const brandId = useMemo(() => state.brandId, [state.brandId]);

  // Create a stable key based on product IDs to detect actual basket changes
  const basketItemsKey = useMemo(() => {
    const ids = basketItems.map(item => item.productId || item.sku || item.id).sort().join(',');
    return ids;
  }, [basketItems]);

  // Reset state when basketItems change (new data layer event)
  useEffect(() => {
    setResult(null);
    setError(null);
    setValidationResult(null);
    setShowConflictModal(false);
    setIsLoading(false);
    setAvailableProducts([]); // Reset available products when basket changes
    setCategorizedItems(null); // Reset categorization
    setIsSelectorOpen(false);
  }, [basketItemsKey]);

  // Loading messages effect - cycles through different messages with more detail
  useEffect(() => {
    if (isLoading) {
      const messages = [
        "Ürünler inceleniyor...",
        "Renk uyumları analiz ediliyor...",
        "Stil kombinasyonları hesaplanıyor...",
        "Görseller hazırlanıyor...",
        "Ürün görselleri birleştiriliyor...",
        "AI modelimiz kombinini tasarlıyor...",
        "Stilinize uygun kombinler oluşturuluyor...",
        "En iyi kombinasyon seçiliyor...",
        "Son dokunuşlar yapılıyor...",
        "Neredeyse hazır..."
      ];

      let currentIndex = 0;
      setLoadingMessage(messages[0]);

      const interval = setInterval(() => {
        currentIndex = (currentIndex + 1) % messages.length;
        setLoadingMessage(messages[currentIndex]);
      }, 2000); // Her 2 saniyede bir mesaj değişir (10 mesaj * 2sn = 20sn)

      return () => clearInterval(interval);
    } else {
      setLoadingMessage("Ürünler inceleniyor...");
    }
  }, [isLoading]);

  // Validation fonksiyonu - Backend'e ID'leri gönder
  const validateProducts = useCallback(async (): Promise<ValidationProduct[] | undefined> => {
    if (basketItems.length < 2) {
      setError("En az 2 ürün gerekli");
      return undefined;
    }

    if (!brandId) {
      setError("Brand bilgisi bulunamadı.");
      return undefined;
    }

    try {
      // Extract SKUs from basket items (priority: item.sku > item.product.sku > item.productId > item.id)
      const skus = basketItems.map(item => {
        // Try SKU first (most common)
        if (item.sku) return item.sku;
        // Try product.sku
        if (item.product?.sku) return item.product.sku;
        // Fallback to productId or id (might be SKU format)
        return item.productId || item.id;
      }).filter(Boolean).filter(sku => typeof sku === 'string' && sku.trim().length > 0);

      if (skus.length === 0) {
        throw new Error('No valid SKUs found in basket items');
      }

      // Backend validation service - use unified backend
      const validationUrl = `${environmentConfig.api.validationApi}/products/bulk`;

      // First try with brandId, if no results, try without brandId filter
      let requestBody: any = {
        skus,
        brandId,
        includeBrand: true,
        includeInactive: false
      };

      let response = await fetch(
        validationUrl,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(`Validation failed: ${response.statusText}`);
      }

      let data = await response.json();

      // If no products found with brandId filter, try without brandId
      if (data.success && data.data?.products?.length === 0 && brandId) {
        requestBody = {
          skus,
          includeBrand: true,
          includeInactive: false
        };

        response = await fetch(
          validationUrl,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          throw new Error(`Validation failed: ${response.statusText}`);
        }

        data = await response.json();

        // If products found without brandId, log warning
        if (data.success && data.data?.products?.length > 0) {
          // Products found but with different brandId
        }
      }

      if (!data.success) {
        throw new Error(data.error || 'Validation failed');
      }

      // Unified API returns products directly in data.products
      const validProducts = data.data?.products || [];

      // Check if all products were found
      if (validProducts.length === 0) {
        setError('Sepetinizdeki ürünler bulunamadı. Lütfen ürünleri kontrol edin.');
        return undefined;
      }

      if (validProducts.length < skus.length) {
        // Some products not found, but continue with available ones
      }

      // Convert to expected format for combination generation
      return validProducts;

    } catch (error) {
      setError(error instanceof Error ? error.message : 'Validation başarısız');
      return undefined;
    }
  }, [basketItems, brandId]);

  const generateCombination = useCallback(async (productsToUse?: ValidationProduct[], place?: string) => {
    if (!brandId) {
      setError("Brand bilgisi bulunamadı. Lütfen tekrar deneyin.");
      return;
    }

    // Eğer ürün listesi verilmemişse validation yap
    let validProducts = productsToUse;
    if (!validProducts) {
      validProducts = await validateProducts();
      if (!validProducts) {
        return; // Validation başarısız veya kullanıcı seçimi gerekli
      }
    }

    setIsLoading(true);
    setError(null);
    setResult(null);
    setShowConflictModal(false);

    try {
      // Extract SKUs from validated products
      const productIds = validProducts.map((product) => product.sku || product.id);

      const requestBody: any = {
        productIds, // Send SKUs instead of productList
        brandId,
      };

      // Add place if provided
      if (place) {
        requestBody.place = place;
      }

      const response = await fetch(
        `${environmentConfig.api.combinationsApi}/combinations/with-bounding-boxes`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      // Handle rate limit (429) - redirect to rate limit page
      if (response.status === 429) {
        navigate('/rate-limit');
        return;
      }

      // Handle server error (500) - redirect to error page
      if (response.status >= 500) {
        navigate('/error');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      if (data.success && data.data) {

        // Check if image generation failed (new endpoint uses generatedImage instead of hasImage)
        if (!data.data.generatedImage) {
          throw new Error("Görsel oluşturulamadı. Lütfen tekrar deneyin.");
        }

        // Build result object from new response format
        const combinationResult = {
          imageData: data.data.generatedImage,
          bounding_boxes: data.data.bounding_boxes || [],
          products: validProducts || [],
          success: true,
          timestamp: new Date().toISOString()
        };

        // Store available products for editing (keep all products user has access to)
        // Store available products for editing (keep all products user has access to)
        if (validProducts && validProducts.length > 0) {
          const productsToAdd = validProducts;

          setAvailableProducts(prevProducts => {
            // If this is the first combination or we don't have products yet, use these
            if (prevProducts.length === 0) {
              return productsToAdd as any;
            } else {
              // Merge with existing products (keep unique by sku)
              const existingSKUs = new Set(prevProducts.map((p: any) => p.sku || p.id));
              const newProducts = productsToAdd.filter((p: any) =>
                !existingSKUs.has(p.sku || p.id)
              );

              if (newProducts.length > 0) {
                return [...prevProducts, ...newProducts as any];
              }
              return prevProducts;
            }
          });
        }

        setResult(combinationResult as any);

        // Log bounding boxes for debugging
        console.log('✅ Bounding boxes received:', data.data.bounding_boxes);
      } else {
        throw new Error(data.error || "Kombin oluşturulamadı");
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : "Bir hata oluştu");
    } finally {
      setIsLoading(false);
    }
  }, [brandId, validateProducts, navigate]);

  // Modal handlers
  const handleConflictConfirm = useCallback((selected: ValidationProduct[]) => {
    generateCombination(selected, selectedPlace);
  }, [generateCombination, selectedPlace]);

  const handleConflictCancel = useCallback(() => {
    setShowConflictModal(false);
    setError('Kombin oluşturma iptal edildi.');
  }, []);

  // Handle place button click
  const handlePlaceClick = useCallback((place: string | undefined) => {
    setSelectedPlace(place);
    // Regenerate combination with new place
    if (basketItems.length >= 2 && brandId) {
      generateCombination(undefined, place);
    }
  }, [basketItems, brandId, generateCombination]);

  // Handle menu actions
  const handleEditClick = useCallback(() => {
    // If we have categorized items, open the custom selector
    if (categorizedItems) {
      if (result && result.products) {
        setSelectorInitialSelection(result.products.map((p: any) => p.sku || p.id));
      }
      setIsSelectorOpen(true);
      return;
    }

    // Fallback to old behavior if no categorization
    if (availableProducts.length === 0) {
      setError('Önce bir kombin oluşturmalısınız');
      return;
    }
    setIsEditModalOpen(true);
  }, [availableProducts, categorizedItems, result]);

  const handleEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
  }, []);

  const handleEditConfirm = useCallback(async (selectedItems: ProductType[]) => {
    setIsEditModalOpen(false);

    // Validate selected products
    if (selectedItems.length < 2) {
      setError("En az 2 ürün gerekli");
      return;
    }

    if (!brandId) {
      setError("Brand bilgisi bulunamadı.");
      return;
    }

    try {
      // Extract SKUs from selected items (backend artık sku field'ı gönderiyor)
      const productIds = selectedItems.map((item, index) => {
        const sku = item.sku || item.id;
        return sku;
      }).filter(Boolean);

      if (productIds.length === 0) {
        throw new Error('Seçili ürünlerin SKU bilgisi bulunamadı. Backend\'den sku field\'ı geldiğinden emin olun.');
      }

      if (productIds.length !== selectedItems.length) {
        // Some products missing SKU
      }

      // Call generateCombination directly with SKUs
      setIsLoading(true);
      setError(null);
      setResult(null);

      const requestBody: any = {
        productIds,
        brandId,
      };

      if (selectedPlace) {
        requestBody.place = selectedPlace;
      }

      const response = await fetch(
        `${environmentConfig.api.combinationsApi}/combinations/with-bounding-boxes`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      // Handle rate limit (429) - redirect to rate limit page
      if (response.status === 429) {
        navigate('/rate-limit');
        return;
      }

      // Handle server error (500) - redirect to error page
      if (response.status >= 500) {
        navigate('/error');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      if (data.success && data.data) {
        if (!data.data.generatedImage) {
          throw new Error("Görsel oluşturulamadı. Lütfen tekrar deneyin.");
        }

        // Build result object from new response format
        const combinationResult = {
          imageData: data.data.generatedImage,
          bounding_boxes: data.data.bounding_boxes || [],
          products: selectedItems,
          success: true,
          timestamp: new Date().toISOString()
        };

        setResult(combinationResult as any);

        // Log bounding boxes for debugging
        console.log('✅ Bounding boxes received:', data.data.bounding_boxes);
      } else {
        throw new Error(data.error || "Kombin oluşturulamadı");
      }

    } catch (error) {
      setError(error instanceof Error ? error.message : 'Kombin oluşturulamadı');
    } finally {
      setIsLoading(false);
    }
  }, [brandId, selectedPlace, navigate]);

  const handlePlaceDropdownToggle = useCallback(() => {
    setIsPlaceDropdownOpen(!isPlaceDropdownOpen);
  }, [isPlaceDropdownOpen]);

  const handlePlaceDropdownClose = useCallback(() => {
    setIsPlaceDropdownOpen(false);
  }, []);

  // Auto-generate combination or show selector when page loads with products
  useEffect(() => {
    const processBasket = async () => {
      // Basic checks to proceed
      if (basketItems.length < 2 || result || isLoading || error || !brandId || showConflictModal || categorizedItems) {
        return;
      }

      setIsLoading(true);
      setLoadingMessage("Ürünler kategorize ediliyor...");

      try {
        // 1. Fetch full product details from DB first (using valid SKUs)
        setLoadingMessage("Ürün bilgileri alınıyor...");
        const validProducts = await validateProducts();

        if (!validProducts || validProducts.length < 2) {
          setIsLoading(false);
          return;
        }

        // Map API response to UI expected format (e.g. images[] -> imageUrl)
        const uiProducts = validProducts.map((p: any) => ({
          ...p,
          // UI components like MatchOutfitSelector might expect 'imageUrl' or 'image'
          // Backend returns 'images' array
          imageUrl: (p.images && p.images.length > 0) ? p.images[0] : (p.imageUrl || p.image || '/placeholder.jpg'),
          // Ensure title/name consistency
          name: p.title || p.name || 'İsimsiz Ürün'
        }));

        setAvailableProducts(uiProducts as any[]); // Store products so selector can use them


        // 2. Categorize items using full product details
        setLoadingMessage("Ürünler kategorize ediliyor...");
        const categorization = await FashMatchService.categorizeBasketItems(validProducts);
        setCategorizedItems(categorization);

        // 3. Routing Logic
        // Check if we can auto-proceed:
        // - At least 2 categories present
        // - All populated categories have exactly 1 item
        const populatedCategories = Object.entries(categorization)
          .filter(([key, items]) => key !== 'other' && items.length > 0);

        const categoryCount = populatedCategories.length;
        const allSingleItems = populatedCategories.every(([_, items]) => items.length === 1);

        if (categoryCount >= 2 && allSingleItems) {
          // Auto-proceed with all single items
          // We can call generate with undefined and let it validate
          generateCombination(undefined, selectedPlace);
        } else {
          // Conflict or too few categories -> Open Selector
          setIsSelectorOpen(true);
        }

      } catch (err) {
        console.error("Categorization failed", err);
        // Fallback to standard validation/generation attempt
        generateCombination(undefined, selectedPlace);
      } finally {
        setIsLoading(false);
      }
    };

    processBasket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basketItemsKey, result, isLoading, error, brandId, showConflictModal, categorizedItems, generateCombination]);

  // Handler for Selector generation
  const handleSelectorGenerate = useCallback((selectedSkus: string[]) => {
    setIsSelectorOpen(false);

    // Convert selected SKUs to ValidationProduct-like objects to pass to generateCombination
    // We need to look up the original items to get their full details
    const selectedProducts = basketItems
      .filter(item => {
        const itemSku = item.sku || (item.product && item.product.sku) || item.productId || item.id;
        return selectedSkus.includes(itemSku);
      })
      .map(item => ({
        id: item.productId || item.id,
        sku: item.sku || (item.product && item.product.sku) || item.productId || item.id,
        imageUrl: item.product?.imageUrl || item.imageUrl, // Handle nested product or flat structure
        category: item.product?.category || item.category,
        title: item.product?.name || item.product?.title || item.title || item.name // Ensure title property
      }));

    if (selectedProducts.length >= 2) {
      generateCombination(selectedProducts as any, selectedPlace);
    } else {
      setError("En az 2 ürün seçmelisiniz");
    }
  }, [basketItems, generateCombination, selectedPlace]);

  if (basketItems.length === 0) {
    return (
      <div className="basket-combination-container">
        <EmptyState message="Sepetinizde ürün bulunmuyor. Lütfen SDK üzerinden ürün bilgilerini gönderin." />
      </div>
    );
  }

  return (
    <div className="basket-combination-container">
      <div className="basket-combination-main-content">
        {/* Menu Button */}
        <MenuButton
          onEditClick={handleEditClick}
          onPlaceClick={handlePlaceDropdownToggle}
        />

        {/* Main Combination Area */}
        <div className="combination-area">
          <CombinationCard
            imageUrl={result?.combinImageUrl}
            imageData={result?.imageData}
            isLoading={isLoading}
            onImageClick={() => setIsImageZoomed(true)}
            bounding_boxes={result?.bounding_boxes}
            products={result?.products}
          >
            {isLoading && <LoadingSpinner message={loadingMessage} />}

            {error && (
              <ErrorMessage
                message={error}
                onRetry={() => generateCombination(undefined, selectedPlace)}
              />
            )}

            {!result && !isLoading && !error && (
              <GenerateButton onClick={() => generateCombination(undefined, selectedPlace)}>
                AI Kombin Oluştur
              </GenerateButton>
            )}

            {result &&
              (result.imageData || result.combinImageUrl) &&
              !isLoading && (
                <Disclaimer text="Oluşturulan görsel fikir verme amaçlıdır.Kontrol edin." />
              )}
          </CombinationCard>
        </div>
      </div>

      {/* Zoom Modal */}
      <ImageZoomModal
        isOpen={isImageZoomed}
        imageUrl={result?.combinImageUrl}
        imageData={result?.imageData}
        onClose={() => setIsImageZoomed(false)}
      />

      {/* Edit Products Modal */}
      <EditProductsModal
        isOpen={isEditModalOpen}
        products={availableProducts}
        currentCombinationSkus={result?.products?.map((p: any) => p.sku || p.id) || []}
        onClose={handleEditModalClose}
        onConfirm={handleEditConfirm}
      />

      {/* New Mirror Outfit Selector */}
      {isSelectorOpen && categorizedItems && (
        <MatchOutfitSelector
          categorizedIds={categorizedItems}
          allProducts={availableProducts}
          initialSelection={selectorInitialSelection}
          onGenerate={handleSelectorGenerate}
          onClose={() => setIsSelectorOpen(false)}
        />
      )}

      {/* Place Dropdown */}
      <PlaceDropdown
        isOpen={isPlaceDropdownOpen}
        selectedPlace={selectedPlace}
        onPlaceChange={handlePlaceClick}
        onClose={handlePlaceDropdownClose}
        isDisabled={isLoading}
      />

      {/* Conflict Resolution Modal */}
      {showConflictModal && validationResult && (
        <ConflictResolutionModal
          validationResult={validationResult}
          onConfirm={handleConflictConfirm}
          onCancel={handleConflictCancel}
        />
      )}
    </div>
  );
};

export default FashMatchPage;
